import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { getSitemapData } from "../lib/api";
import { NewsletterForm } from "../pages/StoreContact";

export default function Footer({ brand }) {
    const [data, setData] = useState({ locations: [], cities: [], blog: [] });

    useEffect(() => {
        getSitemapData()
            .then(setData)
            .catch(() => {});
    }, []);

    return (
        <footer
            data-testid="site-footer"
            className="mt-32 border-t border-zen-ink/10 bg-zen-elevated text-zen-cream"
        >
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                    <div className="md:col-span-3">
                        {brand?.logo && (
                            <img
                                src={brand.logo}
                                alt="ZenZest Cannabis"
                                className="h-12 w-auto mb-6"
                            />
                        )}
                        <p className="font-serif text-2xl font-light leading-tight max-w-xs text-zen-cream">
                            {brand?.tagline ||
                                "Cannabis, treated like the craft it is."}
                        </p>
                        <p className="mt-6 text-sm text-zen-cream-muted max-w-sm leading-relaxed">
                            weeddispensarynearme.com is a directory powered by{" "}
                            <a
                                href={brand?.store_url || "https://zenzest.com"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-zen-olive-light hover:text-zen-cream transition-colors border-b border-zen-olive-light/40"
                            >
                                ZenZest Cannabis
                            </a>
                            , a family-owned, NY OCM-licensed adult-use
                            dispensary serving Queens, Nassau Long Island, and
                            Staten Island.
                        </p>
                        <div className="mt-7">
                            <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive-light mb-3">
                                Newsletter
                            </p>
                            <NewsletterForm testid="newsletter-footer" />
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive-light mb-5">
                            Shop
                        </p>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <Link
                                    to="/menu/queens-long-island"
                                    data-testid="footer-link-menu"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    Live menu
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/cannabis-delivery"
                                    data-testid="footer-link-delivery"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    Same-day delivery
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/specials"
                                    data-testid="footer-link-specials"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    All specials
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/loyalty"
                                    data-testid="footer-link-loyalty"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    ZenCircle rewards
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/community"
                                    data-testid="footer-link-community"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    Community support
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/why-zenzest"
                                    data-testid="footer-link-why"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    Why ZenZest
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/events"
                                    data-testid="footer-link-events"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    Events
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/learn"
                                    data-testid="footer-link-learn"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    Learn
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/strains"
                                    data-testid="footer-link-strains"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    Strain library
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/blog"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    Journal
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/faq"
                                    data-testid="footer-link-faq"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    Cannabis FAQ
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/compare/zenzest-vs-other-dispensary"
                                    data-testid="footer-link-compare"
                                    className="text-zen-cream-muted hover:text-zen-cream"
                                >
                                    ZenZest vs Other Dispensary
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive-light mb-5">
                            Stores
                        </p>
                        <ul className="space-y-4 text-sm">
                            {data.locations.map((l) => {
                                // Map location slug to the URL prefix used by
                                // the per-store contact routes: "queens" or
                                // "staten-island". Falls back to the raw slug.
                                const storePrefix =
                                    l.slug === "queens-long-island"
                                        ? "queens"
                                        : l.slug;
                                return (
                                    <li key={l.slug}>
                                        <Link
                                            to={`/locations/${l.slug}`}
                                            className="text-zen-cream hover:text-zen-cream block font-medium"
                                        >
                                            {l.name}
                                        </Link>
                                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-zen-cream-muted">
                                            <Link
                                                to={`/menu/${l.slug}`}
                                                className="hover:text-zen-olive-light"
                                                data-testid={`footer-link-menu-${l.slug}`}
                                            >
                                                Live menu
                                            </Link>
                                            <span aria-hidden>·</span>
                                            <Link
                                                to={`/brands/${l.slug}`}
                                                className="hover:text-zen-olive-light"
                                                data-testid={`footer-link-brands-${l.slug}`}
                                            >
                                                Brands
                                            </Link>
                                            <span aria-hidden>·</span>
                                            <Link
                                                to={`/${storePrefix}/contact`}
                                                className="hover:text-zen-olive-light"
                                                data-testid={`footer-link-contact-${l.slug}`}
                                            >
                                                Contact
                                            </Link>
                                            {l.slug === "queens-long-island" && (
                                                <>
                                                    <span aria-hidden>·</span>
                                                    <Link
                                                        to="/visit/queens"
                                                        className="hover:text-zen-olive-light"
                                                        data-testid="footer-link-visit-queens"
                                                    >
                                                        Visit flagship
                                                    </Link>
                                                </>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive-light mb-5">
                            Journal
                        </p>
                        <ul className="space-y-3 text-sm">
                            {data.blog.slice(0, 5).map((p) => (
                                <li key={p.slug}>
                                    <Link
                                        to={`/blog/${p.slug}`}
                                        className="text-zen-cream-muted hover:text-zen-cream"
                                    >
                                        {p.title.length > 40
                                            ? p.title.slice(0, 38) + "…"
                                            : p.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive-light mb-5">
                            Dispensary near you · 80+ neighborhoods
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-zen-cream-muted">
                            {data.cities.slice(0, 36).map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`/dispensary-near/${c.slug}`}
                                    className="hover:text-zen-olive-light transition-colors"
                                >
                                    {c.city}
                                </Link>
                            ))}
                            <Link
                                to="/sitemap"
                                className="text-zen-olive-light hover:text-zen-cream"
                            >
                                See all →
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="mt-16 border-t border-zen-cream/10 pt-8 flex flex-col md:flex-row justify-between gap-4">
                    <p className="text-[10px] text-zen-cream-muted tracking-[0.2em] uppercase">
                        © {new Date().getFullYear()} weeddispensarynearme.com ·
                        For adults 21+ · Keep out of reach of children
                    </p>
                    <div className="flex flex-wrap gap-x-5 gap-y-2 text-[10px] text-zen-cream-muted tracking-[0.2em] uppercase">
                        <Link
                            to="/privacy"
                            data-testid="footer-link-privacy"
                            className="hover:text-zen-cream"
                        >
                            Privacy
                        </Link>
                        <Link
                            to="/terms"
                            data-testid="footer-link-terms"
                            className="hover:text-zen-cream"
                        >
                            Terms
                        </Link>
                        <Link
                            to="/accessibility"
                            data-testid="footer-link-accessibility"
                            className="hover:text-zen-cream"
                        >
                            Accessibility
                        </Link>
                        <span>
                            NY OCM · OCM-RETL-24-000247 / -000099
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
